import React, { useMemo } from 'react';
import clsx from 'clsx';
import { Trans, useTranslation } from 'react-i18next';
import { Card, Theme, ThemeColorsModel, Typography, useTheme } from 'ui-components';
import {
  CALC_RESULT_TEST_ID,
  CHART_MIN_SCALE_MAX,
  CHART_STEPS,
  NO_VALUE_PLACEHOLDER,
  RETIREMENT_CALCULATOR_NAMESPACE,
  SAVINGS_PRODUCTS_ORDER,
  SAVINGS_PRODUCT_CHART_COLORS,
  ZUS_CHART_COLOR,
} from '../../tools/RetirementCalculator.const';
import {
  RetirementCalculatorResultPropsModel,
  RetirementChartSeriesModel,
} from '../../tools/RetirementCalculator.model';
import { getChartScaleTicks } from '../../tools/RetirementCalculator.utils';
import { useFormatAmount } from '../../tools/useFormatAmount';
import { RetirementCalculatorChart } from '../../components/RetirementCalculatorChart/RetirementCalculatorChart';

import useStyles from './RetirementCalculatorResult.style';

export const RetirementCalculatorResult: React.FC<RetirementCalculatorResultPropsModel> = ({
  projection,
  className,
}) => {
  const c = useStyles({});
  const theme = useTheme<Theme & { color: ThemeColorsModel }>();
  const [t] = useTranslation(RETIREMENT_CALCULATOR_NAMESPACE, { useSuspense: false });
  const amount = useFormatAmount();

  const pension = projection?.monthlyPension;
  const total = projection?.totalMonthlyPayout ?? pension;
  const scaleTicks = getChartScaleTicks(total, CHART_MIN_SCALE_MAX, CHART_STEPS);
  const savingsBreakdown = projection?.savingsBreakdown;
  const chartColors = theme.color.charts;

  const { barsCount, barCaptions, chartSeries } = useMemo(() => {
    const savingsSeries: RetirementChartSeriesModel[] = SAVINGS_PRODUCTS_ORDER.filter(
      product => (savingsBreakdown?.[product] ?? 0) > 0,
    ).map(product => ({
      key: product,
      name: t(`savings.products.${product}`, product.toUpperCase()),
      color: chartColors[SAVINGS_PRODUCT_CHART_COLORS[product]],
      values: [null, savingsBreakdown?.[product] ?? 0],
    }));

    const nextBarsCount = savingsSeries.length ? 2 : 1;
    const nextChartSeries: RetirementChartSeriesModel[] =
      pension === undefined
        ? []
        : [
            {
              key: 'zus',
              name: t('result.legend', 'ZUS'),
              color: chartColors[ZUS_CHART_COLOR],
              values: Array.from({ length: nextBarsCount }, () => pension),
            },
            ...savingsSeries,
          ];

    const nextBarCaptions = [t('result.bar.zus-only'), t('result.bar.with-savings')].slice(
      0,
      nextBarsCount,
    );

    return {
      barsCount: nextBarsCount,
      barCaptions: nextBarCaptions,
      chartSeries: nextChartSeries,
    };
  }, [chartColors, pension, savingsBreakdown, t]);

  const amountLabel =
    pension !== undefined
      ? amount(pension)
      : t('result.empty-amount', '{{placeholder}} PLN', { placeholder: NO_VALUE_PLACEHOLDER });

  return (
    <div className={clsx(c.root, className)}>
      <Card
        className={clsx(c.card, c.resultCard)}
        testId={`${CALC_RESULT_TEST_ID}-summary`}
      >
        <div className={c.resultHeader}>
          <Typography variant="body1" component="p" fontWeight="bold">
            {t('result.label')}
          </Typography>
          <Typography
            variant="h4"
            component="p"
            fontWeight="bold"
            textColor={pension === undefined ? 'greys.30' : 'greys.80'}
            className={c.resultValue}
            testId={`${CALC_RESULT_TEST_ID}-amount`}
          >
            {amountLabel}
          </Typography>
        </div>
        {projection?.salaryReplacementRate !== undefined && (
          <Typography variant="body2" component="p">
            <Trans
              t={t}
              i18nKey="result.replacement-rate"
              values={{
                rate: Math.round(projection.salaryReplacementRate),
                comfortable: t('result.comfortable-rate', '70'),
              }}
              components={{ accent: <strong className={c.rateAccent} /> }}
            />
          </Typography>
        )}
      </Card>

      <Card
        className={clsx(c.card, c.chartCard)}
        testId={`${CALC_RESULT_TEST_ID}-chart`}
      >
        <div className={c.chart}>
          <RetirementCalculatorChart
            series={chartSeries}
            barCaptions={barCaptions}
            barsCount={barsCount}
            scaleTicks={scaleTicks}
            description={t(
              'result.chart-description',
            )}
          />

          <ul className={c.chartLegend}>
            {chartSeries.map(entry => (
              <li key={entry.key} className={c.chartLegendItem}>
                <span
                  className={c.chartLegendMarker}
                  style={{ backgroundColor: entry.color }}
                  aria-hidden="true"
                />
                <Typography variant="body1" component="span">
                  {entry.name}
                </Typography>
              </li>
            ))}
          </ul>
        </div>

        <p className={c.notice}>
          <Trans
            t={t}
            i18nKey="result.next-step-notice"
            components={{ accent: <strong className={c.noticeAccent} /> }}
          />
        </p>
      </Card>
    </div>
  );
};
